// Pure helpers carved out of codemirror.ts. The host file pulls in
// CodeMirror, web-tree-sitter, and (transitively, via parseAnsi) the
// .dox/* TypeScript chain — none of which the test sandbox can
// resolve. Everything here is closed-form, dependency-free, and safe
// to import from a vitest file.

// ---------------------------------------------------------------------------
// Line-range hash parsing (#L42, #L42-L58, #L42-58)
// ---------------------------------------------------------------------------

export interface LineRange {
  readonly from: number;
  readonly to: number;
}

export const parseLineHash = (hash: string): LineRange | null => {
  const m = hash.match(/^#L(\d+)(?:-L?(\d+))?$/);
  if (!m) return null;
  const from = parseInt(m[1]!, 10);
  const to = m[2] ? parseInt(m[2], 10) : from;
  return { from, to };
};

// Canonical encoder. `from === to` collapses to the short form; the
// long form always uses `-L` so it round-trips through parseLineHash.
export const formatLineHash = (range: LineRange): string =>
  range.from === range.to ? `#L${range.from}` : `#L${range.from}-L${range.to}`;

// ---------------------------------------------------------------------------
// Viewport bounds with 50% margin (smooth scrolling — render slightly
// outside what's strictly visible)
// ---------------------------------------------------------------------------

export interface ViewportRange {
  readonly from: number;
  readonly to: number;
}

export const viewportBounds = (
  visibleRanges: readonly ViewportRange[],
  docLength: number,
): ViewportRange => {
  if (visibleRanges.length === 0) return { from: 0, to: docLength };
  const vFrom = visibleRanges[0]!.from;
  const vTo = visibleRanges[visibleRanges.length - 1]!.to;
  const margin = Math.floor((vTo - vFrom) * 0.5);
  const to = Math.min(docLength, vTo + margin);
  return { from: Math.min(Math.max(0, vFrom - margin), to), to };
};

// ---------------------------------------------------------------------------
// Deterministic blame color: hue from first 6 hex chars of the SHA,
// fixed S=35%, L=55%
// ---------------------------------------------------------------------------

export const shaToHsl = (sha: string): string => {
  const h = parseInt(sha.slice(0, 6), 16) % 360;
  return `hsl(${h}, 35%, 55%)`;
};

// ---------------------------------------------------------------------------
// Minimal replacement — the one edit that turns `before` into `after`
// ---------------------------------------------------------------------------

export interface MinimalChange {
  readonly from: number;
  readonly to: number;
  readonly insert: string;
}

const isHighSurrogate = (code: number): boolean =>
  code >= 0xd800 && code <= 0xdbff;
const isLowSurrogate = (code: number): boolean =>
  code >= 0xdc00 && code <= 0xdfff;

// Trim the common prefix and suffix and replace only what is left, in
// UTF-16 offsets (CodeMirror's). Replacing `0..doc.length` instead would
// be one edit too, but CodeMirror maps the selection through a change,
// and a whole-document change maps every cursor to its edge; a change
// confined to the lines that differ leaves a cursor on an untouched line
// where it was. Neither cut splits a surrogate pair.
export const minimalChange = (before: string, after: string): MinimalChange => {
  const limit = Math.min(before.length, after.length);
  let prefix = 0;
  while (
    prefix < limit &&
    before.charCodeAt(prefix) === after.charCodeAt(prefix)
  )
    prefix++;
  if (prefix > 0 && isHighSurrogate(before.charCodeAt(prefix - 1))) prefix--;

  let suffix = 0;
  while (
    suffix < limit - prefix &&
    before.charCodeAt(before.length - 1 - suffix) ===
      after.charCodeAt(after.length - 1 - suffix)
  )
    suffix++;
  if (suffix > 0 && isLowSurrogate(before.charCodeAt(before.length - suffix)))
    suffix--;

  return {
    from: prefix,
    to: before.length - suffix,
    insert: after.slice(prefix, after.length - suffix),
  };
};
